// 指先ツール (純ロジック)。最初のダブで色を「指」に取り込み、以降のダブで画像へ塗り広げながら
// 指の色を更新する。ストローク終了時にまとめて計算する (ライブプレビューは無い)。
import { DabGenerator, type StrokeSample } from '@/brush/dabs'
import { generateRoundTip } from '@/brush/tip'

export interface SmudgeOptions {
  size: number
  hardness: number
  /** 0..1: 指に残る色の割合 (高いほど遠くまで伸びる) */
  strength: number
  spacing?: number
}

/**
 * img (w×h RGBA、ストレートアルファ) をサンプル列に沿って指先で伸ばした新しい画像を返す。
 * 変更範囲 (画像座標) も返す。サンプルは画像座標。
 */
export function smudgeStroke(img: Uint8ClampedArray, w: number, h: number, samples: StrokeSample[], o: SmudgeOptions): { rgba: Uint8ClampedArray; rect: { x: number; y: number; w: number; h: number } | null } {
  const out = new Uint8ClampedArray(img)
  if (samples.length < 1 || o.size < 1) return { rgba: out, rect: null }
  const gen = new DabGenerator({ size: o.size, spacing: o.spacing ?? 0.25, flow: 1, pressureSize: false, pressureFlow: false, minSize: 0.1 })
  const dabs = samples.flatMap(s => gen.feed(s))
  if (!dabs.length) return { rgba: out, rect: null }
  const TIP = 64
  const tip = generateRoundTip(TIP, o.hardness)
  const R = Math.ceil(o.size / 2)
  const D = R * 2 + 1
  // 指のバッファ (プリマルチプライ float)
  const finger = new Float32Array(D * D * 4)
  const has = new Uint8Array(D * D)
  const s = Math.max(0, Math.min(1, o.strength))
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity
  dabs.forEach((d, di) => {
    const cx = Math.round(d.x), cy = Math.round(d.y)
    for (let dy = -R; dy <= R; dy++) {
      for (let dx = -R; dx <= R; dx++) {
        const r = Math.hypot(dx, dy) / (o.size / 2)
        if (r > 1) continue
        const t = tip[Math.min(TIP - 1, Math.max(0, Math.round((dx / (o.size / 2) * 0.5 + 0.5) * (TIP - 1)))) + TIP * Math.min(TIP - 1, Math.max(0, Math.round((dy / (o.size / 2) * 0.5 + 0.5) * (TIP - 1))))]! / 255
        if (t <= 0) continue
        const x = cx + dx, y = cy + dy
        const fi = (dy + R) * D + (dx + R)
        const inside = x >= 0 && y >= 0 && x < w && y < h
        const pi = inside ? (y * w + x) * 4 : -1
        const a = inside ? out[pi + 3]! / 255 : 0
        const pr = inside ? (out[pi]! / 255) * a : 0, pg = inside ? (out[pi + 1]! / 255) * a : 0, pb = inside ? (out[pi + 2]! / 255) * a : 0
        if (di === 0 || !has[fi]) {
          finger[fi * 4] = pr; finger[fi * 4 + 1] = pg; finger[fi * 4 + 2] = pb; finger[fi * 4 + 3] = a
          has[fi] = 1
          continue
        }
        // 画像 ← 指 (強さ × 先端)、指 ← 画像 (1 - 強さ)
        const k = s * t
        const nr = pr * (1 - k) + finger[fi * 4]! * k, ng = pg * (1 - k) + finger[fi * 4 + 1]! * k, nb = pb * (1 - k) + finger[fi * 4 + 2]! * k, na = a * (1 - k) + finger[fi * 4 + 3]! * k
        if (inside) {
          out[pi + 3] = Math.round(na * 255)
          if (na > 0) { out[pi] = Math.round((nr / na) * 255); out[pi + 1] = Math.round((ng / na) * 255); out[pi + 2] = Math.round((nb / na) * 255) }
          minX = Math.min(minX, x); minY = Math.min(minY, y); maxX = Math.max(maxX, x); maxY = Math.max(maxY, y)
        }
        // 指は塗る前の画素を (1 - 強さ) だけ取り込む (塗った後を取り込むと減衰せず無限に伸びる)
        finger[fi * 4] = finger[fi * 4]! * s + pr * (1 - s)
        finger[fi * 4 + 1] = finger[fi * 4 + 1]! * s + pg * (1 - s)
        finger[fi * 4 + 2] = finger[fi * 4 + 2]! * s + pb * (1 - s)
        finger[fi * 4 + 3] = finger[fi * 4 + 3]! * s + a * (1 - s)
      }
    }
  })
  if (!Number.isFinite(minX)) return { rgba: out, rect: null }
  return { rgba: out, rect: { x: minX, y: minY, w: maxX - minX + 1, h: maxY - minY + 1 } }
}
